import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { wsUrl, api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { playSignalChime, primeAudio, playDmChime, playDenChime, playMateChime, setDmChime, setDenChime, setMateChime } from "../lib/sound";
import { registerServiceWorker, autoResubscribe } from "../lib/push";
import { installOutboxListener } from "../lib/outbox";

const SocketContext = createContext(null);
// Alpha Invisible Mode — tiny, rarely-changing context so every Avatar can grey out its owner's
// own dot without re-rendering on each socket event.
export const InvisibleContext = createContext({ meId: null, invisible: false });
export const useInvisible = () => useContext(InvisibleContext);

export function SocketProvider({ children }) {
  const { user, setUser } = useAuth();
  const wsRef = useRef(null);
  const listenersRef = useRef(new Set());
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);
  const retryRef = useRef(() => {});
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [presenceMap, setPresenceMap] = useState({});
  const [statusMap, setStatusMap] = useState({});
  const [myPresence, setMyPresence] = useState(user?.presence === "away" || user?.presence === "busy" ? user.presence : "online");
  const [myStatus, setMyStatus] = useState(user?.status || "Howling");
  const [invisible, setInvisible] = useState(!!user?.invisible);
  useEffect(() => { setInvisible(!!user?.invisible); }, [user?.invisible]);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("ps_sound") !== "off");
  const soundOnRef = useRef(soundOn);
  const manualPresence = useRef(myPresence);
  const [dmUnread, setDmUnread] = useState(0);
  const [denUnread, setDenUnread] = useState(0);
  const [helpdeskUnread, setHelpdeskUnread] = useState(0);
  const badgeTimer = useRef(null);
  const refreshBadgesRef = useRef(() => {});

  const refreshBadges = useCallback(async () => {
    try {
      const [c, r, g] = await Promise.all([
        api.get("/conversations/unread-total"),
        api.get("/rooms"),
        api.get("/group-dms").catch(() => ({ data: [] })),
      ]);
      const dm = c.data?.unread || 0;
      const grp = (g.data || []).reduce((s, x) => s + (x.unread || 0), 0);
      setDmUnread(dm + grp);
      setDenUnread((r.data || []).reduce((s, x) => s + (x.unread_count || 0), 0));
      api.get("/helpdesk/unread").then((h) => setHelpdeskUnread(h.data?.unread || 0)).catch(() => {});
    } catch (e) { console.debug("Badge refresh failed:", e); }
  }, []);
  refreshBadgesRef.current = refreshBadges;

  const scheduleBadgeRefresh = useCallback(() => {
    if (badgeTimer.current) return;
    badgeTimer.current = setTimeout(() => { badgeTimer.current = null; refreshBadgesRef.current(); }, 500);
  }, []);

  // Sync the installed-app (home screen) icon badge with total unread messages.
  useEffect(() => {
    if (!("setAppBadge" in navigator)) return;
    const total = (dmUnread || 0) + (denUnread || 0);
    if (total > 0) navigator.setAppBadge(total).catch(() => {});
    else navigator.clearAppBadge().catch(() => {});
  }, [dmUnread, denUnread]);

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  const send = useCallback((obj) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(obj));
    }
  }, []);

  const setPresence = useCallback((p, status) => {
    manualPresence.current = p;
    setMyPresence(p);
    if (status !== undefined && status !== null) setMyStatus(status);
    setPresenceMap((m) => ({ ...m, [user?.id]: p }));
    api.post("/presence", { presence: p, status: status ?? undefined })
      .then(() => { if (status !== undefined && status !== null) setUser((u) => (u ? { ...u, status } : u)); })
      .catch(() => {});
  }, [user, setUser]);

  const toggleSound = useCallback(() => {
    const next = !soundOnRef.current;
    soundOnRef.current = next;
    setSoundOn(next);
    localStorage.setItem("ps_sound", next ? "on" : "off");
    // Always unlock audio within this gesture, and chime as confirmation:
    // a rising note when enabling, a soft low note when muting.
    primeAudio();
    playSignalChime(next);
  }, []);

  // load initial presence map + notifications for mates
  useEffect(() => {
    if (user && user !== false) {
      // Seed this device's chime prefs from the wolf's saved profile (source of truth).
      if (user.chime_dm) setDmChime(user.chime_dm);
      if (user.chime_den) setDenChime(user.chime_den);
      if (user.chime_mate) setMateChime(user.chime_mate);
      registerServiceWorker();
      installOutboxListener();
      autoResubscribe(); // silently restore push for wolves who already granted permission
      api.get("/presence").then((r) => setPresenceMap((m) => ({ ...m, ...r.data }))).catch(() => {});
      api.get("/notifications").then((r) => { setNotifications(r.data.notifications); setUnread(r.data.unread); }).catch(() => {});
      refreshBadges();
    } else if (user === false) {
      // Logged out — clear message badges + the installed-app icon badge.
      setDmUnread(0);
      setDenUnread(0);
      if ("clearAppBadge" in navigator) navigator.clearAppBadge().catch(() => {});
    }
  }, [user, refreshBadges]);

  const markNotificationsRead = useCallback(() => {
    setUnread(0);
    setNotifications((ns) => ns.map((n) => ({ ...n, read: true })));
    api.post("/notifications/read").catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || user === false) return;
    let stopped = false;
    let pingInterval;
    let reconnectTimer;
    let silentTimer;
    // A drop survives a reload/app reopen so the server still counts the comeback as a reconnect.
    let hadDrop = sessionStorage.getItem("ps_ws_dropped") === "1";
    let backoff = 1000; // exponential backoff, reset on a successful open
    let rateLimited = false;
    let failedAttempts = 0;
    let authFails = 0;
    const MAX_AUTH_RETRIES = 3;

    const giveUp = (reason) => {
      stopped = true;
      clearTimeout(silentTimer); silentTimer = null;
      setReconnecting(false);
      setGaveUp(true);
      api.post("/ws-giveup", { reason, attempts: failedAttempts }).catch(() => {});
    };

    const scheduleReconnect = () => {
      if (stopped) return;
      hadDrop = true;
      sessionStorage.setItem("ps_ws_dropped", "1");
      failedAttempts += 1;
      // Show the "Reconnecting… tap to retry" banner once 3 quick retries have failed (or after 20s).
      if (failedAttempts >= 3) setReconnecting(true);
      else if (!silentTimer) silentTimer = setTimeout(() => setReconnecting(true), 20000);
      // 1s, 2s, 4s … capped at 30s, plus up to 20% random jitter (≤1s) so clients that dropped
      // on a shared blip (e.g. a server restart) don't reconnect in lockstep.
      const delay = Math.round(backoff + Math.random() * Math.min(1000, backoff * 0.2));
      backoff = Math.min(backoff * 2, 30000);
      reconnectTimer = setTimeout(connect, delay);
    };

    // One dispatcher for WebSocket + SSE events. SSE copies (one-way streams) are de-duplicated by content.
    const seen = new Map();
    function handleEvent(data) {
      // SSE badge mirror of a chat event (no message body) — refresh unread badges from the server only.
      if (data && data.badge_update) {
        if (data.sender_id !== user.id) scheduleBadgeRefresh();
        return;
      }
      if (data && ["notification", "presence", "feed_new_post"].includes(data.type)) {
        const k = JSON.stringify(data);
        const now = Date.now();
        if (seen.has(k) && now - seen.get(k) < 10000) return;
        seen.set(k, now);
        if (seen.size > 300) seen.delete(seen.keys().next().value);
      }
        if (data.type === "notification") {
        setNotifications((n) => [data.notification, ...n].slice(0, 50));
        setUnread((u) => u + 1);
        if (data.notification?.kind === "helpdesk_update") scheduleBadgeRefresh();
        if (soundOnRef.current && data.notification?.kind !== "alpha_shield") {
          const kind = data.notification?.kind;
          if (kind === "mate_request" || kind === "mate_accepted") playMateChime();
          else playSignalChime();
        }
      } else if (data.type === "presence") {
        setPresenceMap((m) => ({ ...m, [data.user_id]: data.presence }));
        if (data.presence === "offline") setStatusMap((m) => { const n = { ...m }; delete n[data.user_id]; return n; });
        else if (data.status) setStatusMap((m) => ({ ...m, [data.user_id]: data.status }));
        if (data.user_id === user.id && data.status) setMyStatus(data.status);
      } else if (data.type === "direct_message") {
        const m = data.message;
        if (m.recipient_id === user.id && m.sender_id !== user.id) {
          if (soundOnRef.current) playDmChime();
          scheduleBadgeRefresh();
        }
      } else if (data.type === "group_message") {
        const m = data.message;
        if (m && m.sender_id !== user.id) {
          if (soundOnRef.current) playDmChime();
          scheduleBadgeRefresh();
        }
      } else if (data.type === "room_message") {
        const m = data.message;
        if (m && m.sender_id !== user.id) {
          if (soundOnRef.current) playDenChime();
          scheduleBadgeRefresh();
        }
      } else if (data.type === "banned") {
        window.location.href = "/enter";
        return;
      } else if (data.type === "force_logout") {
        // An Alpha ended this session — bounce to the login screen immediately.
        window.location.href = "/enter";
        return;
      }
      listenersRef.current.forEach((fn) => fn(data));
    }

    // Server-Sent Events for one-way streams (notification count, presence, new-howl arrivals) so the
    // WebSocket stays dedicated to chat + watch-party sync. EventSource auto-reconnects with Last-Event-ID.
    const sources = [];
    if (typeof EventSource !== "undefined") {
      const base = (process.env.REACT_APP_BACKEND_URL || "") + "/api/sse/";
      ["notifications", "presence", "feed"].forEach((stream) => {
        const es = new EventSource(base + stream, { withCredentials: true });
        const types = stream === "notifications" ? ["notification", "direct_message", "group_message", "room_message"] : ["presence", "feed_new_post"];
        types.forEach((t) => es.addEventListener(t, (ev) => {
          try { handleEvent(JSON.parse(ev.data)); } catch { /* malformed frame */ }
        }));
        // A (re)opened notifications stream may have missed events — reconcile from the server.
        if (stream === "notifications") es.addEventListener("open", () => refreshBadgesRef.current());
        sources.push(es);
      });
    }
    const closeSources = () => {
      sources.forEach((es) => es.close());
      // Phone backgrounded/app closed: say goodbye cleanly (1000) instead of leaving the OS to sever it as a drop.
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) { try { ws.close(1000); } catch { /* already closing */ } }
    };
    window.addEventListener("pagehide", closeSources);

    async function connect() {
      if (stopped) return;
      // Mint a short-lived ticket so the handshake doesn't depend on the httpOnly cookie
      // being forwarded on the WS upgrade. Cookie remains as a server-side fallback.
      let ticket = null;
      try {
        const r = await api.get("/ws-ticket");
        ticket = r.data?.ticket || null;
      } catch (e) {
        console.debug("ws-ticket fetch failed:", e);
      }
      if (stopped) return;
      const qs = [ticket && `token=${encodeURIComponent(ticket)}`, hadDrop && "r=1"].filter(Boolean).join("&");
      const url = qs ? `${wsUrl()}?${qs}` : wsUrl();
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        setReconnecting(false);
        setGaveUp(false);
        clearTimeout(silentTimer); silentTimer = null;
        backoff = 1000; // healthy connection — reset backoff
        rateLimited = false;
        failedAttempts = 0;
        authFails = 0;
        if (hadDrop) {
          hadDrop = false;
          sessionStorage.removeItem("ps_ws_dropped");
          // Events may have been missed while down: drop any queued local refresh and take the server's counts now.
          clearTimeout(badgeTimer.current); badgeTimer.current = null;
          // Let live surfaces (den chat, DMs) quietly re-watch + backfill anything missed during the drop.
          listenersRef.current.forEach((fn) => { try { fn({ type: "ws_reconnected" }); } catch (e) { /* listener error */ } });
        }
        if (manualPresence.current && manualPresence.current !== "online") {
          ws.send(JSON.stringify({ type: "presence", presence: manualPresence.current }));
        }
        // Re-sync presence map + unread badges on every (re)connect so nothing
        // stays stale (e.g. a dot stuck gray) after a brief drop.
        api.get("/presence").then((r) => setPresenceMap((m) => ({ ...m, ...r.data }))).catch(() => {});
        refreshBadgesRef.current();
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
        }, 25000);
      };
      ws.onmessage = (ev) => {
        let data;
        try { data = JSON.parse(ev.data); } catch { return; }
        handleEvent(data);
      };
      ws.onclose = (ev) => {
        setConnected(false);
        clearInterval(pingInterval);
        if (stopped) return;
        // 4401 = stale/missing ticket, 1008 = session refused. Retry a few times with a fresh
        // ticket; for 1008 first confirm the session is still valid, else stop until re-login.
        if (ev && (ev.code === 4401 || ev.code === 1008)) {
          authFails += 1;
          if (authFails > MAX_AUTH_RETRIES) { giveUp(ev.code === 1008 ? "session_refused" : "stale_token"); return; }
          if (ev.code === 1008) {
            api.get("/auth/me").then(() => scheduleReconnect()).catch((e) => {
              if (e?.response?.status === 401) { stopped = true; setReconnecting(false); } else scheduleReconnect();
            });
            return;
          }
        }
        rateLimited = !!(ev && ev.code === 1013); // server throttled this IP — let the backoff run out
        scheduleReconnect();
      };
      ws.onerror = () => {};
    }
    connect();

    // Network back / tab visible again → reconnect right away instead of waiting out the backoff.
    const kick = () => {
      if (stopped || document.visibilityState === "hidden") return;
      const ws = wsRef.current;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
      if (rateLimited) return;
      clearTimeout(reconnectTimer);
      backoff = 1000;
      connect();
    };
    window.addEventListener("online", kick);
    document.addEventListener("visibilitychange", kick);
    retryRef.current = () => {
      setGaveUp(false);
      stopped = false;
      authFails = 0;
      rateLimited = false;
      clearTimeout(reconnectTimer);
      backoff = 1000;
      const ws = wsRef.current;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
      connect();
    };

    return () => {
      stopped = true;
      closeSources();
      window.removeEventListener("pagehide", closeSources);
      window.removeEventListener("online", kick);
      document.removeEventListener("visibilitychange", kick);
      clearInterval(pingInterval);
      clearTimeout(reconnectTimer);
      clearTimeout(silentTimer);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        try { wsRef.current.close(1000); } catch (e) { console.debug("ws close failed", e); }
      }
    };
  }, [user, scheduleBadgeRefresh]);

  // idle auto-away — delay is configurable per wolf (user.away_after minutes; 0 = never)
  useEffect(() => {
    if (!user || user === false) return;
    const minutes = user.away_after == null ? 2 : user.away_after;
    if (!minutes || minutes <= 0) return; // "Never" — skip auto-away entirely
    const delay = minutes * 60000;
    let idleTimer;
    const goAway = () => {
      if (manualPresence.current === "online") {
        setMyPresence("away");
        send({ type: "presence", presence: "away" });
        setPresenceMap((m) => ({ ...m, [user.id]: "away" }));
        manualPresence.current = "away-auto";
      }
    };
    const reset = () => {
      primeAudio();
      clearTimeout(idleTimer);
      if (manualPresence.current === "away-auto") {
        manualPresence.current = "online";
        setMyPresence("online");
        send({ type: "presence", presence: "online" });
        setPresenceMap((m) => ({ ...m, [user.id]: "online" }));
      }
      idleTimer = setTimeout(goAway, delay);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => {
      clearTimeout(idleTimer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, send]);

  const retryConnection = useCallback(() => retryRef.current(), []);
  const clearNotifications = useCallback(() => { setNotifications([]); setUnread(0); }, []);

  // Keep the audio context alive across tab/app backgrounding (iOS/Android suspend it),
  // and re-sync presence + unread badges whenever the app returns to the foreground.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return; // only act on becoming visible
      if (user && user !== false) {
        // Reconcile badges from the server immediately, whatever the WS/SSE state.
        clearTimeout(badgeTimer.current); badgeTimer.current = null;
        refreshBadgesRef.current();
        api.get("/presence").then((r) => setPresenceMap((m) => ({ ...m, ...r.data }))).catch(() => {});
      }
      primeAudio();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible); // restored from the back/forward cache
    return () => { document.removeEventListener("visibilitychange", onVisible); window.removeEventListener("pageshow", onVisible); };
  }, [user]);

  // Safety-net poll: converge unread badges (also in background tabs, for the app icon badge) and
  // presence (foreground only) even if a WS/SSE event was missed.
  useEffect(() => {
    if (!user || user === false) return;
    const id = setInterval(() => {
      refreshBadgesRef.current();
      if (document.visibilityState !== "visible") return;
      api.get("/presence").then((r) => setPresenceMap((m) => ({ ...m, ...r.data }))).catch(() => {});
    }, 15000);
    return () => clearInterval(id);
  }, [user]);

  const value = useMemo(
    () => {
      // While Invisible Mode is on, the Alpha's own light reads offline everywhere — including to themself.
      const effPresenceMap = invisible && user?.id ? { ...presenceMap, [user.id]: "offline" } : presenceMap;
      return { connected, reconnecting, gaveUp, retryConnection, subscribe, send, notifications, unread, markNotificationsRead, clearNotifications, presenceMap: effPresenceMap, statusMap, myPresence: invisible ? "offline" : myPresence, myStatus, setPresence, soundOn, toggleSound, dmUnread, denUnread, helpdeskUnread, refreshBadges, invisible, setInvisible };
    },
    [connected, reconnecting, gaveUp, retryConnection, subscribe, send, notifications, unread, markNotificationsRead, clearNotifications, presenceMap, statusMap, myPresence, myStatus, setPresence, soundOn, toggleSound, dmUnread, denUnread, helpdeskUnread, refreshBadges, invisible, user?.id]
  );

  const invisibleValue = useMemo(() => ({ meId: user?.id || null, invisible }), [user?.id, invisible]);
  return (
    <SocketContext.Provider value={value}>
      <InvisibleContext.Provider value={invisibleValue}>
        {children}
      </InvisibleContext.Provider>
    </SocketContext.Provider>
  );
}

export const useSocket = () => useContext(SocketContext);
